/* eslint-disable max-classes-per-file */
import { includes, reduce } from 'lodash';

import { Feature, MultivariateFeature } from './constants';

type Features = {
    [key in Feature]?: boolean;
};

type InputFeatures = {
    [key: string]: boolean | string;
};

type MultivariateFeatures = {
    [key in MultivariateFeature]?: string;
};

type AvailableFeatureKey<
    T extends MultivariateFeature
> = AvailableMultivariateFeatures[T][number];

const TRUTHY_VALUES = ['true', 'enabled'] as const;
const FALSY_VALUES = ['false', 'disabled'] as const;

const BOOLEAN_VALUES = [...TRUTHY_VALUES, ...FALSY_VALUES] as const;

class AvailableMultivariateFeatures
    implements Record<MultivariateFeature, unknown> {
    [MultivariateFeature.HelperBackgroundColor] = [
        'green',
        'yellow',
        'blue',
    ] as const;
}

const availableMultivariateFeatures = new AvailableMultivariateFeatures();

class FeatureManager {
    private features: Features & MultivariateFeatures = {};

    list = {
        ...Feature,
        ...MultivariateFeature,
    };

    setFeatures(
        input: Promise<InputFeatures> | InputFeatures | undefined
    ): void {
        if (!input) return;

        if (isPromise(input)) {
            input
                .then(rawFeatures => {
                    this.updateFeatures(rawFeatures);
                })
                .catch(err => {
                    console.error(err);
                });
        } else {
            this.updateFeatures(input);
        }
    }

    isActive(input: Feature | Feature[]): boolean {
        const featureNames = Array.isArray(input) ? input : [input];

        return featureNames.every(name => Boolean(this.features[name]));
    }

    isVariantEqual<T extends MultivariateFeature>(
        name: T,
        value: AvailableMultivariateFeatures[T][number]
    ): boolean {
        return this.features[name] === value;
    }

    getVariants<T extends MultivariateFeature>(
        name: T
    ): { [Key in AvailableFeatureKey<T>]: boolean } {
        return reduce(
            availableMultivariateFeatures[name],
            (acc, variant: AvailableFeatureKey<T>) => {
                acc[variant] = this.features[name] === variant;

                return acc;
            },
            {} as { [Key in AvailableFeatureKey<T>]: boolean }
        );
    }

    isDisabled(featureName: Feature | MultivariateFeature): boolean {
        return !this.features[featureName];
    }

    getFeatures(): Features {
        // TODO: Optimize
        return {
            ...this.features,
        };
    }

    private updateFeatures(rawFeatures: InputFeatures): void {
        Object.keys(rawFeatures).forEach(featureName => {
            const rawFeatureValue = rawFeatures[featureName];

            if (
                isSupportedFeature(featureName) &&
                isValidValue(rawFeatureValue, featureName)
            ) {
                this.features[featureName] = includes(
                    BOOLEAN_VALUES,
                    rawFeatureValue
                )
                    ? includes(TRUTHY_VALUES, rawFeatureValue)
                    : (rawFeatureValue as any);
            }
        });
    }
}

const supportedFeatureNames = Object.values<string>({
    ...Feature,
    ...MultivariateFeature,
});

function isSupportedFeature(
    featureName: string
): featureName is Feature | MultivariateFeature {
    const isSupported = supportedFeatureNames.includes(featureName);

    if (!isSupported && !__DEV__) {
        const formattedFeatureNames = supportedFeatureNames.map(
            name => `\n- "${name}"`
        );
        console.warn(
            `[FeatureManager]: Unknown feature passed "${featureName}"\n\nSupported features: ${formattedFeatureNames}`
        );
    }

    return isSupported;
}

function isValidValue(
    value: any,
    featureName: string
): value is boolean | string {
    const isValid = typeof value === 'boolean' || typeof value === 'string';

    if (!isValid) {
        console.error(
            `[FeatureManager]: Invalid value passed for feature: "${featureName}"! Expected boolean or string, but received: ${typeof value}`
        );
    }

    return isValid;
}

function isPromise<T>(obj: PromiseLike<T> | any): obj is PromiseLike<T> {
    return (
        !!obj &&
        (typeof obj === 'object' || typeof obj === 'function') &&
        typeof obj.then === 'function'
    );
}

export default new FeatureManager();
